import createSocket from '@/utils/socket';
import {
  filterLlmJobs,
  filterRunModelJobs,
} from '@/components/myassets/myAssetsJobUtils';
import { filterRelabelInFilterJobs } from '@/components/myboxes/relabelInFilterJobUtils';

const LIVE_TASKS = new Set(['run_model', 'sam3_analyse', 'llm_analyse', 'relabel_in_filter', 'prepare_crop_download']);
const FETCH_TASKS = ['run_model', 'sam3_analyse', 'llm_analyse', 'relabel_in_filter', 'prepare_crop_download'];

function filterCropDownloadJobs(jobs) {
  return (jobs || []).filter((job) => job.task === 'prepare_crop_download' && job.processed === 0 && !job.deleted_at);
}
function projectJobsChannel(projectId) {
  if (!projectId || projectId === 'undefined') {
    return null;
  }
  return `${projectId}_jobs`;
}

function allJobsFromState(jobsById) {
  return Object.values(jobsById || {});
}

const state = {
  socket: null,
  channel: null,
  jobsById: {},
};

const getters = {
  run_model_jobs: (state) => filterRunModelJobs(allJobsFromState(state.jobsById)),
  llm_jobs: (state) => filterLlmJobs(
    allJobsFromState(state.jobsById).filter((job) => job.task === 'sam3_analyse' || job.task === 'llm_analyse'),
  ),
  relabel_jobs: (state) => filterRelabelInFilterJobs(allJobsFromState(state.jobsById)),
  crop_download_jobs: (state) => filterCropDownloadJobs(allJobsFromState(state.jobsById)),
  run_model_count: (_state, getters) => getters.run_model_jobs.length,
  llm_count: (_state, getters) => getters.llm_jobs.length,
  relabel_count: (_state, getters) => getters.relabel_jobs.length,
  crop_download_count: (_state, getters) => getters.crop_download_jobs.length,
};

const mutations = {
  set_socket(state, socket) {
    state.socket = socket;
  },
  set_channel(state, channel) {
    state.channel = channel;
  },
  reset_jobs(state) {
    state.jobsById = {};
  },
  set_jobs(state, jobs) {
    const next = {};
    (jobs || []).forEach((job) => {
      if (job && job.id != null) {
        next[job.id] = job;
      }
    });
    state.jobsById = next;
  },
  upsert_job(state, job) {
    if (!job || job.id == null) {
      return;
    }
    state.jobsById = {
      ...state.jobsById,
      [job.id]: job,
    };
  },
  remove_job(state, jobId) {
    if (jobId == null || !(jobId in state.jobsById)) {
      return;
    }
    const next = { ...state.jobsById };
    delete next[jobId];
    state.jobsById = next;
  },
};

const actions = {
  async ensure_subscribed(context) {
    const projectId = sessionStorage.getItem('project_id');
    const channel = projectJobsChannel(projectId);
    if (!channel) {
      return;
    }
    if (context.state.channel === channel && context.state.socket) {
      return;
    }
    context.dispatch('teardown');
    context.commit('set_channel', channel);
    context.dispatch('setup_socket', channel);
    await context.dispatch('fetch_active');
  },
  async fetch_active(context) {
    try {
      const results = await Promise.all(
        FETCH_TASKS.map((task) => this._vm.$api.job.get_active_jobs(task)),
      );
      const jobs = results.flatMap((taskJobs) => taskJobs || []);
      context.commit('set_jobs', jobs);
    } catch (error) {
      console.error('Failed to load project live jobs', error);
    }
  },
  setup_socket(context, channel) {
    const socket = createSocket();
    context.commit('set_socket', socket);
    socket.on('connect', () => {
      socket.emit('subscribe', channel);
    });
    socket.on('message', (message) => {
      let payload = null;
      try {
        payload = JSON.parse(JSON.parse(message));
      } catch (e) {
        return;
      }
      if (!payload || payload.type !== 'project_job_update' || !payload.job) {
        return;
      }
      context.dispatch('apply_job_update', payload.job);
    });
  },
  apply_job_update(context, job) {
    if (!job || !LIVE_TASKS.has(job.task)) {
      return;
    }
    const done = job.deleted_at || job.processed === 1;
    if (done) {
      context.commit('remove_job', job.id);
      return;
    }
    context.commit('upsert_job', job);
  },
  teardown(context) {
    if (context.state.socket) {
      context.state.socket.close();
    }
    context.commit('set_socket', null);
    context.commit('set_channel', null);
    context.commit('reset_jobs');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
